import React, { useContext, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Tooltip, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { ColorModeContext } from '../../context/ColorModeContext';

const Error404 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);

  const isDark = colorMode?.mode
    ? colorMode.mode === 'dark'
    : theme.palette.mode === 'dark';

  useEffect(() => {
    document.title = 'Page Not Found | mSecure Browser';
  }, []);

  const handleTakeMeHome = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/home');
    } else {
      navigate('/');
    }
  };

  const handleToggleMode = () => {
    if (colorMode?.toggleColorMode) {
      colorMode.toggleColorMode();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '100vh',
        height: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 4,
        boxSizing: 'border-box',
        backgroundColor: isDark ? '#0b1424' : '#f4f7fb',
        transition: 'background-color 0.3s ease',
      }}
    >
      {/* Layer 1: Ambient Bottom Glow Spotlight */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80%',
          pointerEvents: 'none',
          background: isDark
            ? 'radial-gradient(ellipse 85% 65% at 50% 100%, rgba(85, 155, 245, 0.38) 0%, rgba(45, 100, 195, 0.2) 35%, rgba(18, 45, 95, 0.06) 60%, transparent 80%)'
            : 'radial-gradient(ellipse 85% 65% at 50% 100%, rgba(74, 163, 255, 0.35) 0%, rgba(150, 200, 255, 0.22) 35%, rgba(215, 235, 255, 0.1) 60%, transparent 80%)',
          zIndex: 1,
        }}
      />

      {/* Layer 2: Grid Lines Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: isDark
            ? `
              linear-gradient(to right, rgba(255, 255, 255, 0.045) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255, 255, 255, 0.045) 1px, transparent 1px)
            `
            : `
              linear-gradient(to right, rgba(31, 111, 235, 0.065) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(31, 111, 235, 0.065) 1px, transparent 1px)
            `,
          backgroundSize: '56px 56px',
          zIndex: 1,
        }}
      />

      {/* Top Right Theme Toggle Button */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 10,
        }}
      >
        <Tooltip
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          placement="bottom-end"
        >
          <IconButton
            onClick={handleToggleMode}
            aria-label="Toggle color mode"
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(11, 37, 69, 0.06)',
              border: isDark
                ? '1px solid rgba(255, 255, 255, 0.12)'
                : '1px solid rgba(11, 37, 69, 0.12)',
              color: isDark ? '#F8FAFC' : '#0B2545',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.16)'
                  : 'rgba(11, 37, 69, 0.12)',
                transform: 'scale(1.05)',
              },
            }}
          >
            {isDark ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Layer 3: Giant 404 Background Watermark (Clean HTML text rasterization) */}
      <Box
        component="div"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: {
            xs: '140px !important',
            sm: '240px !important',
            md: '330px !important',
            lg: '400px !important',
          },
          fontWeight: '800 !important',
          fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif !important",
          letterSpacing: '-0.04em !important',
          lineHeight: '1 !important',
          color: isDark
            ? 'rgba(255, 255, 255, 0.065) !important'
            : 'rgba(11, 37, 69, 0.055) !important',
          userSelect: 'none',
          pointerEvents: 'none',
          zIndex: 2,
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}
      >
        404
      </Box>

      {/* Layer 4: Foreground Content (Overlaid in center of 404) */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          maxWidth: '560px',
          width: '100%',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Inter', 'Segoe UI', -apple-system, Roboto, sans-serif",
            fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: isDark ? '#FFFFFF' : '#0B2545',
            lineHeight: 1.2,
            mb: { xs: 1.25, sm: 1.5 },
            textAlign: 'center',
          }}
        >
          Page not found
        </Typography>

        <Typography
          sx={{
            fontFamily: "'Inter', 'Segoe UI', -apple-system, Roboto, sans-serif",
            fontSize: { xs: '0.9rem', sm: '0.975rem' },
            fontWeight: 400,
            lineHeight: 1.55,
            color: isDark ? 'rgba(255, 255, 255, 0.68)' : '#64748B',
            textAlign: 'center',
            maxWidth: '440px',
            mb: { xs: 3.25, sm: 3.75 },
            whiteSpace: 'pre-line',
          }}
        >
          {"We can't find the page that you're looking for.\nProbably the link is broken"}
        </Typography>

        <Button
          onClick={handleTakeMeHome}
          disableElevation
          sx={{
            borderRadius: '9999px',
            px: { xs: 3.5, sm: 4 },
            py: { xs: 1.1, sm: 1.25 },
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            fontWeight: 600,
            fontFamily: "'Inter', 'Segoe UI', -apple-system, Roboto, sans-serif",
            textTransform: 'none',
            cursor: 'pointer',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: isDark ? '#FFFFFF' : '#0B2545',
            color: isDark ? '#0B1E38' : '#FFFFFF',
            boxShadow: isDark
              ? '0 4px 16px rgba(0, 0, 0, 0.3)'
              : '0 4px 16px rgba(11, 37, 69, 0.2)',
            '&:hover': {
              backgroundColor: isDark ? '#F1F5F9' : '#1F6FEB',
              transform: 'translateY(-1px)',
              boxShadow: isDark
                ? '0 8px 24px rgba(74, 163, 255, 0.35)'
                : '0 8px 24px rgba(31, 111, 235, 0.35)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          Take me home
        </Button>
      </Box>
    </Box>
  );
};

export default Error404;